import { useDraggable } from '@dnd-kit/core';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import { getEquipmentKindForGridObjectType } from 'components/structure-page/modals/add-equipment-modal-config';
import { EQUIPMENT_GRID_SQUARES, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  GridSpaceDataType,
  LevelType
} from 'data/types';
import { openEquipmentModal, setHoveredConcealedZone } from 'features/ui';
import { useEquipmentContextMenu } from 'hooks/use-equipment-context-menu';
import { memo, useCallback, useMemo } from 'react';
import { getAirHandlerTypeDisplayLabel } from 'utils/air-handler-input-helpers';
import { squareVisualRotationDeg } from 'utils/equipment-rotation';
import {
  gridObjectRotationTransform,
  gridObjectTransform
} from 'utils/grid-object-transform';
import { findZoneUniqueIdByZoneItemUniqueId } from 'utils/configurations';

type ValidationStatus = {
  isValid: boolean;
  hasWarning: boolean;
  messages: string[];
};

type GridObjectProps = {
  object: {
    id: string;
    itemId: string;
    position: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    type:
      | 'condenser'
      | 'electrical-panel'
      | 'air-handler'
      | 'existing-condenser'
      | 'existing-furnace'
      | 'placement-marker';
    label?: string;
    airHandlerTypeCode?: AirHandlerTypeCodeType;
    placementType?: 'ground' | 'wall' | 'roof';
    overlay?: boolean;
    overlayDirection?: 'above' | 'below';
    tooltip?: string;
    gridSquares?: number;
    rotation?: number;
  };
  configurationId?: string;
  gridSpaces?: GridSpaceDataType[];
  structure?: LevelType[];
  validationStatus?: ValidationStatus;
  zoom?: number;
  mode?: string;
  readOnly?: boolean;
};

const OBJECT_CONFIG = {
  condenser: {
    label: 'C',
    key: 'condenser',
    dragType: 'grid-condenser'
  },
  'electrical-panel': {
    label: 'EP',
    key: 'electricalPanel',
    dragType: 'grid-electrical-panel'
  },
  'air-handler': {
    label: 'AH',
    key: 'airHandler',
    dragType: 'grid-air-handler'
  },
  'existing-condenser': {
    label: 'C',
    key: 'existingEquipment',
    dragType: 'grid-existing-equipment'
  },
  'existing-furnace': {
    label: 'F',
    key: 'existingEquipment',
    dragType: 'grid-existing-equipment'
  },
  'placement-marker': {
    label: '',
    key: 'placementMarker',
    dragType: 'placement-marker'
  }
} as const;

function GridObject({
  object,
  configurationId,
  gridSpaces = [],
  structure = [],
  validationStatus,
  zoom = 1,
  mode,
  readOnly = false
}: GridObjectProps) {
  const dispatch = useAppDispatch();
  const { configurations } = useAppSelector((state) => state.configurations);
  const { openEquipmentContextMenu } = useEquipmentContextMenu({
    object,
    configurationId,
    gridSpaces,
    structure
  });
  const objectConfig = OBJECT_CONFIG[object.type];

  const isConfigurationPage = mode === 'zones' || mode === 'static';
  const isElectricalPanelOnConfigPage =
    object.type === 'electrical-panel' && isConfigurationPage;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: object.id,
      data: {
        type: objectConfig.dragType,
        [objectConfig.key]: object
      },
      disabled: readOnly || isElectricalPanelOnConfigPage || !!object.overlay
    });

  const hasValidationError = validationStatus && !validationStatus.isValid;
  const hasValidationWarning =
    validationStatus && validationStatus.hasWarning && validationStatus.isValid;

  const airHandlerTooltipText = useMemo(() => {
    if (object.type !== 'air-handler' || !object.airHandlerTypeCode)
      return null;
    return getAirHandlerTypeDisplayLabel(object.airHandlerTypeCode);
  }, [object]);

  const condenserTooltipText = useMemo(() => {
    if (object.type !== 'condenser') return null;
    if (object.tooltip) return object.tooltip;
    const placement = object.placementType
      ? object.placementType.charAt(0).toUpperCase() +
        object.placementType.slice(1)
      : null;
    return placement ? `Condenser - ${placement}` : 'Condenser';
  }, [object]);

  const sizeLabel = useMemo(() => null, []);

  const frameClasses = clsx(
    'rounded border-2 flex items-center justify-center text-sm font-medium',
    object.overlay
      ? 'border-dashed border-gray-300 bg-transparent text-gray-400'
      : [
          hasValidationError &&
            'border-red-500 bg-red-100 text-red-900 ring-2 ring-red-300',
          hasValidationWarning &&
            'border-amber-500 bg-amber-100 text-amber-900 ring-2 ring-amber-300',
          !hasValidationError &&
            !hasValidationWarning &&
            object.type === 'condenser' &&
            'border-purple-500 bg-purple-100 text-purple-900',
          object.type === 'electrical-panel' &&
            'border-yellow-500 bg-yellow-100 text-yellow-900',
          object.type === 'existing-condenser' &&
            'border-purple-500 bg-purple-100 text-purple-900',
          object.type === 'existing-furnace' &&
            'border-orange-500 bg-orange-100 text-orange-900',
          !hasValidationError &&
            !hasValidationWarning &&
            object.type === 'air-handler' &&
            'border-green-500 bg-green-100 text-green-900',
          isDragging ? 'shadow-lg opacity-80' : 'shadow-md opacity-100'
        ]
  );
  let interactionClasses = 'z-[20] cursor-grab';
  if (isDragging) {
    interactionClasses = 'z-[1000] cursor-grabbing';
  } else if (isElectricalPanelOnConfigPage || readOnly) {
    interactionClasses = 'z-[20] cursor-default';
  }

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (readOnly) return;

      e.preventDefault();
      e.stopPropagation();

      openEquipmentContextMenu(e.clientX, e.clientY);
    },
    [readOnly, openEquipmentContextMenu]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // AHA-6681 batch 2 addendum: a placed panel's properties (amperage)
      // stay editable from the placement step — clicking the icon opens the
      // same modal the Existing Equipment page's pencil button uses.
      if (object.type === 'electrical-panel') {
        if (readOnly || isConfigurationPage) {
          return;
        }
        e.stopPropagation();
        dispatch(
          openEquipmentModal({
            kind: 'electrical-panel',
            editComponentId: object.itemId
          })
        );
        return;
      }
      if (
        object.type !== 'existing-condenser' &&
        object.type !== 'existing-furnace'
      ) {
        return;
      }
      e.stopPropagation();
      dispatch(
        openEquipmentModal({
          kind: getEquipmentKindForGridObjectType(object.type),
          editComponentId: object.itemId
        })
      );
    },
    [dispatch, object.type, object.itemId, readOnly, isConfigurationPage]
  );

  const gridSquares = object.gridSquares ?? EQUIPMENT_GRID_SQUARES;
  const objectSize = gridSquares * STRUCTURE_GRID_SIZE;
  const targetX = object.position.x * STRUCTURE_GRID_SIZE;
  const targetY = object.position.y * STRUCTURE_GRID_SIZE;
  const rotationDeg = squareVisualRotationDeg(object.rotation ?? 0);

  const style: React.CSSProperties = {
    left: targetX,
    top: targetY,
    width: objectSize,
    height: objectSize,
    transform: gridObjectTransform(transform, zoom)
  };

  const displayLabel = object.label || objectConfig.label;

  if (object.type === 'placement-marker') {
    return (
      <div
        className='pointer-events-none absolute z-[20] flex items-center justify-center text-red-500'
        style={style}
        data-grid-object
      >
        <XMarkIcon className='h-full w-full' strokeWidth={2.5} />
      </div>
    );
  }

  if (object.overlay) {
    return (
      <div
        className={clsx('pointer-events-none absolute z-[0]', frameClasses)}
        style={style}
        data-grid-object
      >
        <div className='flex flex-col items-center'>
          <span className='text-xs leading-none'>{displayLabel}</span>
          <span className='text-[10px] leading-none text-gray-400'>
            {object.overlayDirection === 'above' ? 'Above' : 'Below'}
          </span>
        </div>
      </div>
    );
  }

  const isConcealedAirHandler =
    object.type === 'air-handler' &&
    (object.airHandlerTypeCode === 'concealed' ||
      object.airHandlerTypeCode === 'concealed_high_static');

  const handleConcealedHover = (hovering: boolean) => {
    if (!isConcealedAirHandler) return;
    if (!hovering) {
      dispatch(setHoveredConcealedZone(null));
      return;
    }
    const config = configurations.find((c) => c.uniqueId === configurationId);
    dispatch(
      setHoveredConcealedZone(
        findZoneUniqueIdByZoneItemUniqueId(
          config,
          String(object.id).replace('-air-handler', '')
        )
      )
    );
  };

  return (
    <div
      ref={setNodeRef}
      className={clsx('absolute', interactionClasses)}
      style={style}
      data-grid-object
      {...attributes}
      {...listeners}
      onContextMenu={handleContextMenu}
      onClick={handleClick}
      onMouseEnter={() => handleConcealedHover(true)}
      onMouseLeave={() => handleConcealedHover(false)}
    >
      <div
        data-grid-object-frame
        className={clsx('h-full w-full', frameClasses)}
        style={{ transform: gridObjectRotationTransform(rotationDeg) }}
      >
        <div
          className='flex items-center justify-center'
          style={{ transform: gridObjectRotationTransform(-rotationDeg) }}
        >
          {sizeLabel ? (
            <div className='flex flex-col items-center space-y-0'>
              <span className='text-xs leading-none'>{displayLabel}</span>
              <span className='whitespace-nowrap text-xs leading-none tracking-tight'>
                {sizeLabel}
              </span>
            </div>
          ) : (
            <span>{displayLabel}</span>
          )}
        </div>
      </div>
      {validationStatus && validationStatus.messages.length > 0 && (
        <Tooltip
          text={
            validationStatus.messages.length === 1
              ? validationStatus.messages[0]
              : undefined
          }
        >
          {validationStatus.messages.length > 1 ? (
            <ul className='list-disc pl-4'>
              {validationStatus.messages.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          ) : undefined}
        </Tooltip>
      )}
      {object.type === 'air-handler' && airHandlerTooltipText && (
        <Tooltip text={airHandlerTooltipText} />
      )}
      {object.type === 'condenser' && condenserTooltipText && (
        <Tooltip text={condenserTooltipText} />
      )}
      {(object.type === 'existing-condenser' ||
        object.type === 'existing-furnace') &&
        object.tooltip && <Tooltip text={object.tooltip} />}
      {object.type === 'electrical-panel' && (
        <Tooltip text='Electrical Panel' />
      )}
    </div>
  );
}

export default memo(GridObject);
